import { useId, type FormEventHandler, type ReactNode } from 'react'
import { Box } from '@mui/material'
import type { SystemStyleObject, Theme } from '@mui/system'

import Field, { fieldControlSx, fieldErrorId } from '@base/Field/Field.component'

export interface SelectProps {
  id?: string
  name?: string
  label?: ReactNode
  error?: string | null
  defaultValue?: string
  value?: string
  onChange?: FormEventHandler<HTMLSelectElement>
  dataTestId?: string
  children: ReactNode
}

// Styled native <select> sharing the Field frame. The control reuses
// `fieldControlSx` and adds a painted chevron (native `appearance: none`
// strips the default arrow on every engine). Merged into a single sx object —
// the `component='select'` Box overload rejects the array form.
const Select = ({
  id,
  name,
  label,
  error,
  defaultValue,
  value,
  onChange,
  dataTestId = 'select',
  children,
}: SelectProps) => {
  // Fall back to a generated id so the <label htmlFor> stays associated with
  // the control even when the caller omits an id (mirrors TextField).
  const reactId = useId()
  const fieldId = id ?? reactId
  const errorId = error ? fieldErrorId(fieldId) : undefined
  return (
    <Field id={fieldId} label={label} error={error} dataTestId={dataTestId}>
      <Box
        component='select'
        id={fieldId}
        name={name}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        aria-invalid={error ? true : undefined}
        aria-describedby={errorId}
        data-testid={`${dataTestId}-control`}
        sx={{
          ...(fieldControlSx as SystemStyleObject<Theme>),
          cursor: 'pointer',
          appearance: 'none',
          padding: '0 36px 0 14px',
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='%23999' d='M1 1.5 6 6.5l5-5'/></svg>\")",
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 14px center',
        }}
      >
        {children}
      </Box>
    </Field>
  )
}

export default Select
